import { useId, useRef, useState, type KeyboardEvent } from 'react';
import {
  useFloating,
  useClick,
  useDismiss,
  useListNavigation,
  useInteractions,
  offset,
  flip,
  shift,
  size as floatingSize,
  autoUpdate,
  FloatingPortal,
  FloatingFocusManager,
} from '@floating-ui/react';
import { HugeiconsIcon, type IconSvgElement } from '@hugeicons/react';
import { ArrowDown01Icon, Cancel01Icon, FilterIcon, Tick02Icon } from '@hugeicons/core-free-icons';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectPreset {
  label: string;
  value: readonly string[];
}

interface MultiSelectFilterProps {
  /** The dimension, shown when several values are ticked ("Severity"). */
  name: string;
  /** Shown while the filter sits at its default ("All severity"). */
  idleLabel: string;
  options: MultiSelectOption[];
  value: readonly string[];
  onChange: (value: string[]) => void;
  /** The resting selection. Anything else turns the field blue with a count. */
  defaultValue?: readonly string[];
  /** One-click selections listed above the checkboxes (Status: Active / All). */
  presets?: MultiSelectPreset[];
  /** Label for the footer link that returns to the default. */
  resetLabel?: string;
  disabled?: boolean;
  className?: string;
  /** Compact editable summary for applied issue filters. */
  selectionSummary?: boolean;
  icon?: IconSvgElement;
  onClear?: () => void;
  'aria-label': string;
}

const sameSet = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v) => b.includes(v));

/**
 * Checkbox filter for the dashboard Issues page (Emily, 2026-09-24; prototype
 * docs/prototypes/issues-filter-checkboxes.html). Same trigger box, menu
 * surface and keyboard model as SelectDropdown, but the menu stays open while
 * values are ticked. At its default the field reads `idleLabel` in grey with no
 * count, so a default view (Status resting on Active) never looks filtered.
 * Changed, it turns blue, reads the one ticked value or the dimension name, and
 * shows how many are ticked.
 */
export function MultiSelectFilter({
  name,
  idleLabel,
  options,
  value,
  onChange,
  defaultValue = [],
  presets = [],
  resetLabel = 'Clear',
  disabled = false,
  className = '',
  selectionSummary = false,
  icon = FilterIcon,
  onClear,
  'aria-label': ariaLabel,
}: MultiSelectFilterProps) {
  const id = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const listRef = useRef<Array<HTMLElement | null>>([]);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen && !disabled,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(4), flip(), shift({ padding: 8 }), floatingSize({
      padding: 8,
      apply({ availableHeight, availableWidth, rects, elements }) {
        Object.assign(elements.floating.style, {
          minWidth: `${Math.min(Math.max(rects.reference.width, 224), availableWidth)}px`,
          maxWidth: `${Math.max(0, availableWidth)}px`,
          maxHeight: `${Math.max(0, Math.min(320, availableHeight))}px`,
        });
      },
    })],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const listNavigation = useListNavigation(context, { listRef, activeIndex, onNavigate: setActiveIndex, loop: true });
  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([click, dismiss, listNavigation]);

  // Nothing ticked reads as the default too: it lets a reviewer untick the
  // defaults and then tick one value, instead of the field fighting back.
  const changed = value.length > 0 && !sameSet(value, defaultValue);
  const single = value.length === 1 ? options.find((o) => o.value === value[0]) : undefined;
  const selectedLabels = value.map(selected => options.find(option => option.value === selected)?.label ?? 'Unavailable choice');
  const label = !changed ? idleLabel : selectionSummary
    ? `${name}: ${selectedLabels[0]}${value.length > 1 ? ` +${value.length - 1}` : ''}`
    : single ? single.label : name;
  const fullLabel = changed && selectionSummary ? `${name}: ${selectedLabels.join(', ')}` : label;

  const toggle = (v: string) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  const rows = [
    ...presets.map((p) => ({ key: `preset:${p.label}`, kind: 'preset' as const, preset: p })),
    ...options.map((o) => ({ key: o.value, kind: 'option' as const, option: o })),
  ];

  function onItemKeyDown(event: KeyboardEvent, run: () => void) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      run();
    }
  }

  return (
    <>
      <span className={onClear ? 'inline-flex w-max min-w-0 max-w-56' : 'contents'}>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? `${id}-options` : undefined}
        aria-label={changed ? `${ariaLabel}, ${value.length} selected` : ariaLabel}
        title={fullLabel}
        className={`input input-toolbar flex ${onClear ? 'min-w-0 flex-1 rounded-r-none border-r-0' : 'w-auto min-w-0 max-w-56'} items-center gap-2 text-left ${changed ? 'border-primary-500 bg-primary-50' : ''} ${className}`}
      >
        <HugeiconsIcon
          icon={icon}
          size={14}
          strokeWidth={2}
          color="currentColor"
          className={`h-3.5 w-3.5 shrink-0 ${changed ? 'text-primary-700' : 'text-gray-600'}`}
          aria-hidden="true"
        />
        <span className={`min-w-0 flex-1 truncate font-normal ${changed ? 'text-primary-700' : 'text-gray-700'}`}>{label}</span>
        {changed && !selectionSummary && (
          <span
            data-selected-count=""
            aria-hidden="true"
            className="flex h-4 min-w-[16px] shrink-0 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] font-semibold text-white"
          >
            {value.length}
          </span>
        )}
        <HugeiconsIcon
          icon={ArrowDown01Icon}
          size={12}
          strokeWidth={2}
          color="currentColor"
          className={`h-3 w-3 shrink-0 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {onClear && <button type="button" disabled={disabled} aria-label={`Clear ${name.toLowerCase()} filter`}
        className="input input-toolbar flex w-[var(--oq-control-app-toolbar-height)] shrink-0 items-center justify-center rounded-l-none border-l-0 border-primary-500 bg-primary-50 px-0 text-primary-700 hover:bg-primary-100"
        onClick={onClear}>
        <HugeiconsIcon icon={Cancel01Icon} size={12} strokeWidth={2} className="h-3 w-3" aria-hidden="true" />
      </button>}
      </span>

      {isOpen && !disabled && (
        <FloatingPortal>
          <FloatingFocusManager context={context} modal={false}>
            <ul
              ref={refs.setFloating}
              id={`${id}-options`}
              role="listbox"
              aria-multiselectable="true"
              aria-label={ariaLabel}
              style={floatingStyles}
              {...getFloatingProps()}
              className="z-50 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg"
            >
              {rows.map((row, index) => {
                const isPreset = row.kind === 'preset';
                const selected = isPreset ? sameSet(value, row.preset.value) : value.includes(row.option.value);
                const run = () => (isPreset ? onChange([...row.preset.value]) : toggle(row.option.value));
                const divider = isPreset && index === presets.length - 1;
                return (
                  <li
                    key={row.key}
                    role="option"
                    aria-selected={selected}
                    tabIndex={activeIndex === index ? 0 : -1}
                    ref={(node) => { listRef.current[index] = node; }}
                    {...getItemProps({ onClick: run, onKeyDown: (e) => onItemKeyDown(e, run) })}
                    className={`flex cursor-pointer items-center gap-3 px-3 py-2 oq-text-body outline-none transition-colors ${
                      activeIndex === index ? 'bg-gray-50' : ''
                    } ${divider ? 'mb-1 border-b border-gray-100' : ''} ${isPreset && selected ? 'text-primary-700' : 'text-gray-700'}`}
                  >
                    {!isPreset && (
                      <span
                        aria-hidden="true"
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                          selected ? 'border-primary-500 bg-primary-500 text-white' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {selected && <HugeiconsIcon icon={Tick02Icon} size={12} strokeWidth={3} color="currentColor" className="h-3 w-3" />}
                      </span>
                    )}
                    <span className="flex-1">{isPreset ? row.preset.label : row.option.label}</span>
                    {isPreset && <span className="flex h-3.5 w-3.5 shrink-0" aria-hidden="true">
                      {selected && <HugeiconsIcon icon={Tick02Icon} size={14} strokeWidth={2} />}
                    </span>}
                  </li>
                );
              })}
              {changed && (
                <li role="presentation" className="mt-1 border-t border-gray-100 px-3 pb-1 pt-2">
                  <button
                    type="button"
                    onClick={() => onChange([...defaultValue])}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    <span className="text-cap-trim block">{resetLabel}</span>
                  </button>
                </li>
              )}
            </ul>
          </FloatingFocusManager>
        </FloatingPortal>
      )}
    </>
  );
}
